// A Step 2 cell's steps as the heatmap and its hover card show them (CellCard.tsx).
import type {
  AmazonBuilding,
  AmazonNeed,
  BuildingStep,
} from "../../api/hooks/useAmazonOverview";
import type { StoryStep } from "./blueprint";
import {
  type Cell,
  firstSentence,
  lineOf,
  namesSend,
  type NeedBy,
  repeatsBold,
  SENT_TO,
  shownCell,
  shownLine,
  type Tone,
} from "./cells";
import { day, formatDate } from "./dates";
import { stepItems } from "./needs";
import { type Overrides, stepKey } from "./overrides";
import { type Owner, stepById } from "./pathSteps";
import styles from "./amazonOverview.module.css";
import { UPLOAD_ID } from "../../utils/uploadId";

export const TONE_CLASS: Record<Tone, string> = {
  done: styles.toneDone,
  open: styles.toneOpen,
  blocked: styles.toneBlocked,
  none: styles.toneNone,
  na: styles.toneNa,
};

export interface Named {
  title: string;
  owner: Owner;
  ownerLabel?: string;
}

/** A step's name and owner as Step 1 shows them. */
export const namer =
  (steps: StoryStep[]) =>
  (id: string): Named =>
    steps.find((s) => s.id === id) ?? stepById(id);

export interface Shown {
  id: string;
  cell: Cell;
  step: Named;
  /** The building's record for the step, overrides applied. */
  record: BuildingStep;
  line: string;
  /** The line is the column's explanation: the record says nothing of its own. */
  means: boolean;
  /** What the card opens to list as needed from Amazon, as Step 3's card for the step
   * lists it (``stepItems``): its items and its open feedback points, in one list. */
  items?: AmazonNeed[];
  /** Each step's needed-by date, which dates the items. */
  need: NeedBy;
}

/** Step ``id`` on ``b`` as its Step 2 cell and hover card show it, ``o`` applied, with
 * each step's needed-by date (``need``) where the page has worked them out. */
export const shownPart = (
  b: AmazonBuilding,
  o: Overrides,
  id: string,
  named: (id: string) => Named,
  need: NeedBy = null,
): Shown => {
  const cell = shownCell(b, id, o, need);
  return {
    id,
    cell,
    step: named(id),
    record: b.steps[id],
    line: shownLine(b, id, o),
    means: lineOf(b, id).means && !o.has(stepKey(id, "line")),
    items: stepItems(b, id).length ? stepItems(b, id) : undefined,
    need,
  };
};

/** Steps done by an email from Amazon's side, which the card names whether or not it is on
 * record. Who sent it is not recorded yet. The guarantee's card names its email only where
 * one is cited: Legal names the guarantee in words, so a missing email says nothing. */
const EMAILED_BY_AMAZON = new Set(["dr3"]);

/** What a dated step rests on: the email's subject and its InRange sender, or that none
 * is cited, so a send is never claimed without the message behind it. ``means`` marks the
 * line that only says nothing is cited. */
/** An upload citation, ``[<upload_id> "words"]``, as agents write them. */
const CITATION = new RegExp(String.raw`\[${UPLOAD_ID}[^\]]*\]`, "g");

export const proofOf = (id: string, step: BuildingStep): { text: string; means: boolean } | null => {
  const quoted = (x: string) => `\u201c${x}\u201d`;
  const proof = (text: string) => ({ text, means: false });
  // The roof's proof is the message its history entry cites.
  if (id === "roof") {
    if (!step.date || (step.state !== "open" && step.state !== "done")) return null;
    return proof(step.source ? `Email: ${quoted(step.source)}` : "Email not on record");
  }
  if (EMAILED_BY_AMAZON.has(id) && step.state === "done") {
    return proof(step.source ? `Email: ${quoted(step.source)}` : "Email not on record");
  }
  if (id === "pcg") return step.source ? proof(`Email: ${quoted(step.source)}`) : null;
  if (!step.date || (step.state !== "open" && step.state !== "done")) return null;
  if (namesSend(id, step)) {
    const on = formatDate(day(step.date));
    // Recorded before the record named its email: Legal's own note is the proof, without
    // the citations its reason carries.
    if (!step.source && !step.sent_by && step.basis === "recorded") {
      const said = firstSentence(step.detail.replace(CITATION, "").replace(/\s+/g, " "));
      return proof(`${SENT_TO[id]} on ${on}, as Legal recorded it, no email linked: ${said}`);
    }
    const by = step.sent_by ? ` by ${step.sent_by}` : ", sender not on record";
    const note = step.note ? `. ${step.note}` : "";
    return proof(
      `${SENT_TO[id]} on ${on}${by}: ${step.source ? quoted(step.source) : "email not on record"}${note}`,
    );
  }
  if (!step.source && !step.sent_by) {
    return step.state === "open" ? { text: "No email cited for the send.", means: true } : null;
  }
  const email = step.source ? `Email: ${quoted(step.source)}` : "No email cited";
  return proof(`${email}${step.sent_by ? ` \u00b7 sent by ${step.sent_by}` : ""}`);
};

/** The hover card's lines under its title, in the order the card shows them. */
export const CARD_LINES = [
  "status",
  "note",
  "lines",
  "more",
  "proof",
  "items",
  "line",
] as const;
export type CardLine = (typeof CARD_LINES)[number];

/** The status, the lines that list items rather than repeat it in words, and the proof:
 * what a step rests on (its email, its sender, or that neither is on record) is evidence,
 * shown whatever the status says. */
const LISTS: ReadonlySet<CardLine> = new Set(["status", "items", "proof"]);

/** Lines a step's hover card leaves out: the structural assessment's card says the report
 * is delivered and how much of the roof it finds viable, never its email or the report's
 * finding in full (Noaa, 25 Oct 2026). The building's own page keeps the finding. */
const LEFT_OUT: Readonly<Record<string, ReadonlySet<CardLine>>> = {
  structural: new Set(["proof", "line"]),
};

/** Whether step ``id``'s hover card leaves ``line`` out, so the edit pane says where a
 * field it drives shows instead. */
export const cardLeavesOut = (id: string, line: CardLine): boolean =>
  LEFT_OUT[id]?.has(line) ?? false;

/** Each line ``part``'s hover card shows, as text: what the card renders and what the edit
 * pane compares to say which field drives which line. A line the card leaves out is absent,
 * including one under the status that says nothing the status does not (``repeatsBold``). */
export const cardTexts = (part: Shown): Map<CardLine, string> => {
  const out = new Map<CardLine, string>();
  const status = part.cell.status;
  const put = (k: CardLine, v: string | undefined) => {
    if (LEFT_OUT[part.id]?.has(k)) return;
    if (v && (LISTS.has(k) || !repeatsBold(v, status))) out.set(k, v);
  };
  put("status", status);
  put("note", part.cell.note && firstSentence(part.cell.note));
  put("lines", part.cell.lines?.join("\n"));
  put("more", part.cell.more && `${part.cell.more.label}: ${part.cell.more.text}`);
  put("proof", proofOf(part.id, part.record)?.text);
  put("items", part.items?.map((n) => `${n.key}:${n.status}`).join(","));
  put("line", part.line);
  return out;
};
